// BankAccount.jsx
import { Helmet } from "react-helmet";
import { useState, useCallback, useMemo } from "react";
import {
    FaChevronRight, FaChevronLeft, FaAnglesLeft, FaAnglesRight,
    FaHashtag, FaMagnifyingGlass, FaFilter, FaUser,
    FaPlus, FaEye, FaXmark, FaPen, FaTrashCan, FaCopy,
    FaBuildingColumns, FaCreditCard,
    FaCheck,
} from "react-icons/fa6";
import { FaCheckCircle, FaTimesCircle, FaSpinner, FaUniversity } from "react-icons/fa";
import { BtnDefault } from "../../../../components/buttons/BtnDefault";
import { Header } from "../../../../components/Header";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { BankAccountCreate } from "./create";
import { useHookBankAccount } from "../../../../hooks/useHookBankAccount";
import Pagination from "../../../../components/pagination/pagination";
import { BankAccountUpdate } from "./update";
import { BankAccountDeleteConfirm } from "./delete";

/* ============================================
   DUMMY DATA
   ============================================ */
const DUMMY_DATA = [
    {
        id: "90e403e8-b1d5-11f1-976e-00163e799c01",
        company_id: "comp-001",
        bank_name: "BCA",
        bank_code: "014",
        account_number: "32564856952",
        account_holder_name: "ZAKI MUBAROK",
        branch: "Jakarta Pusat",
        is_active: true,
        description: "Rekening utama perusahaan",
        created_at: "2024-11-15T08:30:00Z",
        updated_at: "2025-01-10T14:22:00Z",
    },
    {
        id: "a1b2c3d4-e5f6-11f1-976e-00163e799c02",
        company_id: "comp-001",
        bank_name: "BRI",
        bank_code: "002",
        account_number: "008901000567",
        account_holder_name: "ZAKI MUBAROK",
        branch: "Bandung",
        is_active: true,
        description: "Rekening operasional",
        created_at: "2024-12-01T10:00:00Z",
        updated_at: "2025-01-08T09:15:00Z",
    },
    {
        id: "b2c3d4e5-f6a7-11f1-976e-00163e799c03",
        company_id: "comp-001",
        bank_name: "Mandiri",
        bank_code: "008",
        account_number: "1280004567890",
        account_holder_name: "PT DANELA NET",
        branch: "Surabaya",
        is_active: true,
        description: "Rekening payroll karyawan",
        created_at: "2025-01-02T07:45:00Z",
        updated_at: "2025-01-12T16:30:00Z",
    },
    {
        id: "c3d4e5f6-a7b8-11f1-976e-00163e799c04",
        company_id: "comp-001",
        bank_name: "BNI",
        bank_code: "009",
        account_number: "0215678901",
        account_holder_name: "PT DANELA NET",
        branch: "Yogyakarta",
        is_active: false,
        description: "Rekening cadangan (nonaktif)",
        created_at: "2024-10-20T12:00:00Z",
        updated_at: "2024-12-28T11:00:00Z",
    },
    {
        id: "d4e5f6a7-b8c9-11f1-976e-00163e799c05",
        company_id: "comp-001",
        bank_name: "BSI",
        bank_code: "451",
        account_number: "7195678902",
        account_holder_name: "ZAKI MUBAROK",
        branch: "Semarang",
        is_active: true,
        description: "Rekening zakat & infaq",
        created_at: "2025-01-05T09:30:00Z",
        updated_at: "2025-01-14T08:45:00Z",
    },
    {
        id: "e5f6a7b8-c9d0-11f1-976e-00163e799c06",
        company_id: "comp-001",
        bank_name: "CIMB Niaga",
        bank_code: "022",
        account_number: "800123456700",
        account_holder_name: "PT DANELA NET",
        branch: "Jakarta Selatan",
        is_active: true,
        description: "Rekening giro bisnis",
        created_at: "2025-01-08T13:20:00Z",
        updated_at: "2025-01-15T10:10:00Z",
    },
    {
        id: "f6a7b8c9-d0e1-11f1-976e-00163e799c07",
        company_id: "comp-001",
        bank_name: "Permata",
        bank_code: "013",
        account_number: "732015678900",
        account_holder_name: "ZAKI MUBAROK",
        branch: "Medan",
        is_active: false,
        description: "Rekening lama (ditutup)",
        created_at: "2024-08-10T06:00:00Z",
        updated_at: "2024-11-30T17:00:00Z",
    },
    {
        id: "a7b8c9d0-e1f2-11f1-976e-00163e799c08",
        company_id: "comp-001",
        bank_name: "Bank DKI",
        bank_code: "111",
        account_number: "1156789012",
        account_holder_name: "PT DANELA NET",
        branch: "Jakarta Barat",
        is_active: true,
        description: "Rekening pajak daerah",
        created_at: "2025-01-10T15:00:00Z",
        updated_at: "2025-01-16T12:30:00Z",
    },
];

/* ============================================
   Helpers
   ============================================ */
const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return (
        d.toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }) +
        " " +
        d.toLocaleTimeString("id-ID", {
            hour: "2-digit",
            minute: "2-digit",
        })
    );
};

const BANK_ICON_MAP = {
    BCA: "#003399",
    BRI: "#00529C",
    Mandiri: "#003876",
    BNI: "#F15A22",
    BSI: "#00A650",
    "CIMB Niaga": "#7B0C1E",
    Permata: "#0055A5",
    "Bank DKI": "#FDDC00",
};

const getBankColor = (name) => BANK_ICON_MAP[name] || "#0ea5e9";

/* ============================================
   Skeleton
   ============================================ */
const SkeletonRow = ({ cols = 7 }) => (
    <tr className="animate-pulse">
        {Array.from({ length: cols }).map((_, i) => (
            <td key={i} className="px-4 py-3">
                <div
                    className="h-3.5 bg-slate-700/60 rounded-md"
                    style={{ width: `${40 + Math.random() * 50}%` }}
                />
            </td>
        ))}
    </tr>
);

const SkeletonCard = () => (
    <div
        className="rounded-2xl overflow-hidden animate-pulse"
        style={{
            background: "rgba(15, 23, 42, 0.5)",
            border: "1px solid rgba(51, 65, 85, 0.3)",
        }}
    >
        <div
            className="px-4 py-3 flex justify-between items-center"
            style={{
                background: "rgba(15, 23, 42, 0.7)",
                borderBottom: "1px solid rgba(51, 65, 85, 0.2)",
            }}
        >
            <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-slate-700/60" />
                <div className="h-3 bg-slate-700/50 rounded-md w-20" />
            </div>
            <div className="h-6 bg-slate-700/40 rounded-full w-14" />
        </div>
        <div className="p-4">
            <div className="flex justify-between items-center mb-3">
                <div className="space-y-2">
                    <div className="h-5 bg-slate-700/60 rounded-md w-32" />
                    <div className="h-3 bg-slate-700/30 rounded-md w-24" />
                </div>
                <div className="text-right space-y-1">
                    <div className="h-2.5 bg-slate-700/30 rounded w-10 ml-auto" />
                    <div className="h-6 bg-slate-700/50 rounded-md w-24 ml-auto" />
                </div>
            </div>
        </div>
    </div>
);

/* ============================================
   Status Badge
   ============================================ */
const StatusBadge = ({ active }) => {
    if (active) {
        return (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide bg-emerald-500/12 text-emerald-400 border border-emerald-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                Aktif
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide bg-red-500/12 text-red-400 border border-red-500/20">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
            Nonaktif
        </span>
    );
};

/* ============================================
   Bank Icon
   ============================================ */
const BankIcon = ({ name, size = 16 }) => {
    const color = getBankColor(name);
    return (
        <div
            className="flex items-center justify-center rounded-lg flex-shrink-0"
            style={{
                width: size + 8,
                height: size + 8,
                background: `${color}18`,
                border: `1px solid ${color}30`,
            }}
        >
            <FaUniversity size={size * 0.65} style={{ color }} />
        </div>
    );
};

/* ============================================
   Toast
   ============================================ */
const Toast = ({ message, type, onClose }) => {
    const ok = type === "success";
    return (
        <div
            className={`fixed top-6 right-6 z-[100] flex items-start gap-3 px-5 py-4 rounded-xl shadow-2xl max-w-md border transition-all duration-300 ${ok
                ? "bg-emerald-950/90 border-emerald-500/30"
                : "bg-red-950/90 border-red-500/30"
                }`}
            style={{ backdropFilter: "blur(16px)" }}
        >
            {ok ? (
                <FaCheckCircle className="text-emerald-400 text-lg mt-0.5 flex-shrink-0" />
            ) : (
                <FaTimesCircle className="text-red-400 text-lg mt-0.5 flex-shrink-0" />
            )}
            <p className={`text-sm font-medium ${ok ? "text-emerald-200" : "text-red-200"}`}>
                {message}
            </p>
            <button
                onClick={onClose}
                className={`flex-shrink-0 ml-2 ${ok
                    ? "text-emerald-400/60 hover:text-emerald-300"
                    : "text-red-400/60 hover:text-red-300"
                    }`}
            >
                <FaXmark className="text-sm" />
            </button>
        </div>
    );
};

/* ============================================
   Modal Create / Edit
   ============================================ */


/* ============================================
   Modal Delete Confirm
   ============================================ */


/* ============================================
   Drawer Detail
   ============================================ */
const DrawerDetail = ({ open, onClose, data }) => {
    if (!open || !data) return null;

    const bankColor = getBankColor(data.bank_name);

    return (
        <>
            <div
                className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                onClick={onClose}
            />
            <div
                className={`fixed top-0 right-0 z-50 h-full w-full max-w-md shadow-2xl transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"
                    }`}
                style={{
                    background: "rgba(15, 23, 42, 0.97)",
                    borderLeft: "1px solid rgba(51, 65, 85, 0.4)",
                }}
            >
                {/* Header */}
                <div
                    className="px-5 py-4 flex items-center justify-between sticky top-0 z-10"
                    style={{
                        background: "rgba(15, 23, 42, 0.97)",
                        borderBottom: "1px solid rgba(51, 65, 85, 0.3)",
                    }}
                >
                    <div className="flex items-center gap-2.5">
                        <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{
                                background: "rgba(0, 180, 216, 0.12)",
                                border: "1px solid rgba(0, 180, 216, 0.2)",
                            }}
                        >
                            <FaBuildingColumns className="text-sm text-cyan-400" />
                        </div>
                        <h3 className="text-sm font-bold text-gray-100">
                            Detail Rekening
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-gray-100 hover:bg-slate-700/50 transition-all"
                    >
                        <FaXmark className="text-sm" />
                    </button>
                </div>

                {/* Content */}
                <div
                    className="overflow-y-auto p-5 space-y-5"
                    style={{ height: "calc(100% - 65px)" }}
                >
                    {/* Bank Header */}
                    <div
                        className="rounded-xl p-5 text-center"
                        style={{
                            background: `${bankColor}08`,
                            border: `1px solid ${bankColor}20`,
                        }}
                    >
                        <BankIcon name={data.bank_name} size={28} />
                        <h2
                            className="text-lg font-extrabold mt-3"
                            style={{ color: bankColor }}
                        >
                            {data.bank_name}
                        </h2>
                        {data.bank_code && (
                            <p className="text-xs text-slate-500 font-mono mt-0.5">
                                Kode: {data.bank_code}
                            </p>
                        )}
                        <div className="mt-3">
                            <StatusBadge active={data.is_active} />
                        </div>
                    </div>

                    {/* Account Info */}
                    <div
                        className="rounded-xl p-4 space-y-3"
                        style={{
                            background: "rgba(51, 65, 85, 0.1)",
                            border: "1px solid rgba(51, 65, 85, 0.2)",
                        }}
                    >
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                            Informasi Rekening
                        </p>
                        <InfoRow
                            label="Nomor Rekening"
                            value={data.account_number}
                            icon={<FaHashtag className="text-xs text-slate-500" />}
                            mono
                        />
                        <InfoRow
                            label="Pemilik Rekening"
                            value={data.account_holder_name}
                            icon={<FaUser className="text-xs text-slate-500" />}
                        />
                        <InfoRow label="Cabang" value={data.branch || "-"} />
                        <InfoRow
                            label="Keterangan"
                            value={data.description || "-"}
                        />
                    </div>

                    {/* Timestamps */}
                    <div
                        className="rounded-xl p-4 space-y-3"
                        style={{
                            background: "rgba(51, 65, 85, 0.1)",
                            border: "1px solid rgba(51, 65, 85, 0.2)",
                        }}
                    >
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                            Metadata
                        </p>
                        <InfoRow label="ID" value={data.id} mono />
                        <InfoRow label="Dibuat" value={formatDateTime(data.created_at)} />
                        <InfoRow
                            label="Diperbarui"
                            value={formatDateTime(data.updated_at)}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

const InfoRow = ({ label, value, icon, mono }) => (
    <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
            {icon}
            <span className="text-xs text-slate-500">{label}</span>
        </div>
        <span
            className={`text-sm font-medium text-gray-200 text-right truncate max-w-[60%] ${mono ? "font-mono tracking-wide" : ""
                }`}
        >
            {value}
        </span>
    </div>
);

/* ============================================
   Row Actions
   ============================================ */
const RowActions = ({
    item,
    onEdit,
    onDelete,
    onCopyAccount,
    onOpenDetail,
}) => {
    return (
        <div className="flex items-center gap-0.5">
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenDetail();
                }}
                className="p-2 rounded-lg text-slate-400 hover:text-gray-100 hover:bg-slate-700/40 transition-all"
                title="Detail"
            >
                <FaEye className="text-[11px]" />
            </button>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onEdit(item);
                }}
                className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all"
                title="Edit"
            >
                <FaPen className="text-[11px]" />
            </button>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onCopyAccount(item.account_number);
                }}
                className="p-2 rounded-lg text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all"
                title="Salin No. Rekening"
            >
                <FaCopy className="text-[11px]" />
            </button>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onDelete(item);
                }}
                className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
                title="Hapus"
            >
                <FaTrashCan className="text-[11px]" />
            </button>
        </div>
    );
};

/* ============================================
   KOMPONEN UTAMA
   ============================================ */
export const BankAccount = () => {
    const [showSidebar, setShowSidebar] = useState(true);
    const [showModalUpdate, setShowModalUpdate] = useState(false);
    const [showModalDelete, setShowModalDelete] = useState(false);

    const [selectedData, setSelectedData] = useState(null);

    const { data, meta, loading, error, params, setParams, refreshData } = useHookBankAccount();

    // Search & filter
    const [search, setSearch] = useState("");
    const [filterStatus, setFilterStatus] = useState("all"); // all | active | inactive

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 5;

    // Modals
    const [showFormModal, setShowFormModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Drawer
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerData, setDrawerData] = useState(null);

    // Toast
    const [toast, setToast] = useState(null);

    const showToast = useCallback((message, type = "success") => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    }, []);


    /* --- CRUD Handlers --- */
    const handleCreate = () => {
        setEditData(null);
        setShowFormModal(true);
    };

    const handleEdit = (item) => {
        setEditData(item);
        setShowFormModal(true);
    };

    const handleDeleteClick = (item) => {
        setDeleteTarget(item);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = () => {
        setDeleteLoading(true);
        setTimeout(() => {
            // setData((prev) => prev.filter((item) => item.id !== deleteTarget.id));
            setDeleteLoading(false);
            setShowDeleteModal(false);
            showToast("Rekening bank berhasil dihapus");
            setDeleteTarget(null);
        }, 600);
    };

    const handleCopyAccount = (accountNumber) => {
        navigator.clipboard
            .writeText(accountNumber)
            .then(() => showToast("Nomor rekening disalin ke clipboard"))
            .catch(() => showToast("Gagal menyalin", "error"));
    };

    const handleOpenDetail = (item) => {
        setDrawerData(item);
        setDrawerOpen(true);
    };

    const handleCloseDetail = () => {
        setDrawerOpen(false);
        setTimeout(() => setDrawerData(null), 300);
    };

    /* --- Columns --- */
    const columns = [
        { key: "no", label: "No", align: "center", width: "55px" },
        { key: "status", label: "Status", align: "left", width: "120px" },
        { key: "bank_name", label: "Bank", align: "left" },
        { key: "account_number", label: "No. Rekening", align: "left" },
        { key: "account_holder_name", label: "Atas Nama", align: "left" },
        { key: "branch", label: "Cabang", align: "left" },
        { key: "description", label: "Keterangan", align: "left" },
        { key: "actions", label: "Aksi", align: "center", width: "130px" },
    ];


    return (
        <>
            <Helmet>
                <title>Rekening Bank - DANELA NET</title>
            </Helmet>

            <DashboardLayout
                showSidebar={showSidebar}
                setShowSidebar={setShowSidebar}
            >
                <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
                    <div className="flex items-center gap-3">
                        <h2 className="text-base md:text-xl font-bold text-gray-100">
                            Rekening Bank
                        </h2>
                        <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                            {data?.length} rekening
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <BtnDefault onClick={handleCreate}>
                            <FaPlus className="text-xs hidden sm:block" />
                            <span>Tambah Data</span>
                        </BtnDefault>
                    </div>
                </Header>

                <main className="p-4 lg:p-6 space-y-5">
                    {/* Search & Filter */}
                    <section
                        className="rounded-2xl p-4 sm:p-5"
                        style={{
                            background: "rgba(15, 23, 42, 0.5)",
                            border: "1px solid rgba(51, 65, 85, 0.3)",
                            backdropFilter: "blur(12px)",
                        }}
                    >
                        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
                            <div className="w-full lg:max-w-xs flex-shrink-0">
                                <label
                                    htmlFor="search-bank"
                                    className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider"
                                >
                                    Cari Data
                                </label>
                                <div className="relative">
                                    <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                                    <input
                                        id="search-bank"
                                        type="text"
                                        placeholder="Bank, no. rekening, pemilik..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                                    />
                                </div>
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <FaFilter className="text-xs text-slate-500" />
                                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wAider">
                                        Filter
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                                    {/* Status Filter */}
                                    <div>
                                        <select
                                            value={filterStatus}
                                            onChange={(e) => setFilterStatus(e.target.value)}
                                            className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                                        >
                                            <option value="all">Semua Status</option>
                                            <option value="active">Aktif</option>
                                            <option value="inactive">Nonaktif</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ===== DESKTOP TABLE ===== */}
                    <section
                        className="hidden lg:block rounded-2xl overflow-hidden"
                        style={{
                            background: "rgba(15, 23, 42, 0.5)",
                            border: "1px solid rgba(51, 65, 85, 0.3)",
                        }}
                    >
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr
                                        className="border-b"
                                        style={{
                                            background: "rgba(15, 23, 42, 0.7)",
                                            borderColor: "rgba(51, 65, 85, 0.3)",
                                        }}
                                    >
                                        {columns.map((col) => (
                                            <th
                                                key={col.key}
                                                className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${col.align === "center"
                                                    ? "text-center"
                                                    : "text-left"
                                                    }`}
                                                style={col.width ? { width: col.width } : undefined}
                                            >
                                                {col.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody
                                    className="divide-y"
                                    style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}
                                >
                                    {/* {loading &&
                                        !data &&
                                        [...Array(3)].map((_, i) => (
                                            <SkeletonRow key={i} cols={columns.length} />
                                        ))}
                                    {!loading && paginatedData.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={columns.length}
                                                className="px-4 py-16 text-center"
                                            >
                                                <div className="flex flex-col items-center gap-3">
                                                    <div
                                                        className="w-16 h-16 rounded-2xl flex items-center justify-center"
                                                        style={{
                                                            background: "rgba(51, 65, 85, 0.2)",
                                                            border: "1px solid rgba(51, 65, 85, 0.2)",
                                                        }}
                                                    >
                                                        <FaBuildingColumns className="text-2xl text-slate-600" />
                                                    </div>
                                                    <p className="text-slate-400 font-medium">
                                                        Tidak ada rekening ditemukan
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    )} */}

                                    {data &&
                                        data?.map((item, index) => (
                                            <tr
                                                key={item.id}
                                                className="cursor-pointer transition-colors duration-150"
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.background =
                                                        "rgba(0, 180, 216, 0.04)";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background = "transparent";
                                                }}
                                            >
                                                <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs">
                                                    {meta?.from + index}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {item?.primary == 1 ? <FaCheck className="text-green-500" /> : <FaTimesCircle className="text-red-500" />}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2.5">
                                                        <BankIcon name={item.bank_name} size={14} />
                                                        <div>
                                                            <span className="font-semibold text-gray-100 text-sm">
                                                                {item.bank_name}
                                                            </span>
                                                            {item.bank_code && (
                                                                <span className="text-slate-600 font-mono text-[10px] ml-1.5">
                                                                    ({item.bank_code})
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="font-mono text-xs text-cyan-400/80 bg-cyan-500/8 px-2 py-0.5 rounded tracking-wider">
                                                        {item.account_number}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 min-w-[180px]">
                                                    <div className="flex items-center gap-2">
                                                        <FaUser className="text-xs text-slate-500" />
                                                        <span className="font-semibold text-gray-100 uppercase text-xs">
                                                            {item.account_holder_name}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-slate-400 text-xs">
                                                    {item.branch || "-"}
                                                </td>
                                                <td className="px-4 py-3 text-slate-500 text-xs max-w-[200px] truncate">
                                                    {item.description || "-"}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <RowActions
                                                        item={item}
                                                        onEdit={() => {
                                                            setSelectedData(item);
                                                            setShowModalUpdate(true);
                                                        }}
                                                        onDelete={(e) => {
                                                            setShowModalDelete(true);
                                                            setSelectedData(item);
                                                        }}
                                                        onCopyAccount={handleCopyAccount}
                                                        onOpenDetail={() => handleOpenDetail(item)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <Pagination
                            meta={meta}
                            params={params}
                            setParams={setParams}
                        />
                    </section>

                    {/* ===== MOBILE CARDS ===== */}
                    <section className="lg:hidden space-y-3">
                        {loading &&
                            !data &&
                            [...Array(3)].map((_, i) => <SkeletonCard key={i} />)}
                        {!loading && data?.length === 0 && (
                            <div
                                className="rounded-2xl p-12 text-center"
                                style={{
                                    background: "rgba(15, 23, 42, 0.5)",
                                    border: "1px solid rgba(51, 65, 85, 0.3)",
                                }}
                            >
                                <p className="text-slate-400 font-medium">
                                    Tidak ada rekening ditemukan
                                </p>
                            </div>
                        )}
                        {!loading &&
                            data?.map((item) => (
                                <article
                                    key={item.id}
                                    onClick={() => handleOpenDetail(item)}
                                    className="rounded-2xl overflow-hidden active:scale-[0.98] transition-all duration-200 cursor-pointer"
                                    style={{
                                        background: "rgba(15, 23, 42, 0.5)",
                                        border: "1px solid rgba(51, 65, 85, 0.3)",
                                    }}
                                >
                                    {/* Card Header */}
                                    <div
                                        className="px-4 py-3 flex justify-between items-center"
                                        style={{
                                            background: "rgba(15, 23, 42, 0.7)",
                                            borderBottom: "1px solid rgba(51, 65, 85, 0.2)",
                                        }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <BankIcon name={item.bank_name} size={12} />
                                            <span className="text-xs font-bold text-slate-300">
                                                {item.bank_name}
                                            </span>
                                            {item.bank_code && (
                                                <span className="text-[10px] text-slate-600 font-mono">
                                                    ({item.bank_code})
                                                </span>
                                            )}
                                        </div>
                                        <StatusBadge active={item.is_active} />
                                    </div>

                                    {/* Card Body */}
                                    <div className="p-4">
                                        <div className="flex justify-between items-start mb-3">
                                            <div className="space-y-1 flex-1">
                                                <h3 className="font-mono font-semibold text-cyan-400 leading-tight text-sm tracking-wide">
                                                    {item.account_number}
                                                </h3>
                                                <div className="flex items-center gap-1.5 text-slate-500">
                                                    <FaUser className="text-[11px]" />
                                                    <span className="text-xs font-medium uppercase">
                                                        {item.account_holder_name}
                                                    </span>
                                                </div>
                                            </div>
                                            {item.branch && (
                                                <div className="text-right ml-4">
                                                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider leading-none mb-1">
                                                        Cabang
                                                    </p>
                                                    <p className="text-xs font-medium text-slate-400">
                                                        {item.branch}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Card Actions */}
                                        <div
                                            className="flex items-center justify-between pt-3"
                                            style={{
                                                borderTop: "1px solid rgba(51, 65, 85, 0.15)",
                                            }}
                                        >
                                            <div className="flex items-center gap-1.5">
                                                <button
                                                    onClick={(e) => {
                                                        setSelectedData(item);
                                                        setShowModalUpdate(true);
                                                    }}
                                                    className="p-1.5 rounded text-slate-400 hover:text-cyan-400 transition-all"
                                                >
                                                    <FaPen className="text-xs" />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCopyAccount(item.account_number);
                                                    }}
                                                    className="p-1.5 rounded text-slate-400 hover:text-emerald-400 transition-all"
                                                >
                                                    <FaCopy className="text-xs" />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowModalDelete(true);
                                                        setSelectedData(item);
                                                    }}
                                                    className="p-1.5 rounded text-slate-400 hover:text-red-400 transition-all"
                                                >
                                                    <FaTrashCan className="text-xs" />
                                                </button>
                                            </div>
                                            <div className="flex items-center gap-1 text-cyan-400 font-semibold text-xs">
                                                Detail <FaChevronRight className="text-[10px]" />
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        {/* Mobile Pagination */}
                        {meta?.last_page > 1 && (
                            <div
                                className="flex items-center justify-between pt-3 pb-6 px-1"
                                style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}
                            >
                                <button
                                    onClick={() =>
                                        setCurrentPage((p) => Math.max(1, p - 1))
                                    }
                                    disabled={currentPage === 1}
                                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30"
                                    style={{
                                        background: "rgba(15, 23, 42, 0.5)",
                                        border: "1px solid rgba(51, 65, 85, 0.3)",
                                    }}
                                >
                                    <FaChevronLeft className="text-xs" /> Sebelumnya
                                </button>
                                <span className="text-sm font-medium text-slate-400">
                                    <span className="text-cyan-400">{currentPage}</span>
                                    <span className="text-slate-600 mx-1">/</span>
                                    {meta?.last_page}
                                </span>
                                <button
                                    onClick={() =>
                                        setCurrentPage((p) => Math.min(meta?.last_page, p + 1))
                                    }
                                    disabled={currentPage === meta?.last_page}
                                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30"
                                    style={{
                                        background: "rgba(15, 23, 42, 0.5)",
                                        border: "1px solid rgba(51, 65, 85, 0.3)",
                                    }}
                                >
                                    Berikutnya <FaChevronRight className="text-xs" />
                                </button>
                            </div>
                        )}
                    </section>
                </main>
            </DashboardLayout>

            {/* ===== MODALS ===== */}
            <BankAccountCreate
                open={showFormModal}
                setOpen={setShowFormModal}
                refreshData={refreshData}
            />

            <BankAccountUpdate
                open={showModalUpdate}
                setOpen={setShowModalUpdate}
                refreshData={refreshData}
                data={selectedData}
            />

            <BankAccountDeleteConfirm
                open={showModalDelete}
                setOpen={setShowModalDelete}
                refreshData={refreshData}
                data={selectedData}
            />

            <DrawerDetail
                open={drawerOpen}
                onClose={handleCloseDetail}
                data={drawerData}
            />

            {/* Toast */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </>
    );
};